import pl from 'pluralize';
import {
  completeProgressBar,
  Logger,
  newProgressBar,
  progress,
} from '~/util/logger';
import ContensisRepository from '~/repositories/ContensisRepository';
import {
  IUpdateTag,
  IUpdateTagGroup,
  Tag,
  TagGroup,
  TagGroupListOptions,
} from 'contensis-management-api';
import RepositoryBaseService from './RepositoryBaseService';
import { GetTagsArgs } from './TagsMigrationService';
import { TagGroupBuilder } from '~/transformations/TagGroupBuilder';
import { TagBuilder } from '~/transformations/TagBuilder';
import { replaceItemByKey, replaceItemsById } from '~/util/array';
import { isArray, isString } from '~/util/typeguards';

class TagsService extends RepositoryBaseService {
  groups: TagGroup[] = [];
  error: AppError | null = null;
  raw: Tag[] = [];

  /** Keep a record of built groups to migrate */
  migrateGroups: TagGroupBuilder[] = [];
  /** Keep a record of built tags to migrate */
  migrateTags: TagBuilder[] = [];

  /**
   * Resolves a tag we have fetched
   * @param tagId the id of the tag to resolve
   * @returns the resolved tag or undefined if not found
   */
  resolve = (tagId: string) => this.raw.find((t) => t.id === tagId);
  /**
   * Resolves a tag by its label from those we have fetched
   * @param label the label of the tag to resolve
   * @returns the resolved tag or undefined if not found
   */
  resolveByLabel = (
    label: string,
    language = this.contensis.project?.primaryLanguage || 'en-GB',
    tags = this.raw
  ) =>
    tags.find(
      (t) => t.label[language]?.toLowerCase() === label.toLowerCase()
    );

  constructor(contensis: ContensisRepository, source: ContensisRepository) {
    super(contensis, source);
  }

  GetTagGroups = async (
    idOrOptions?: string | (TagGroupListOptions & { ids?: string[] })
  ): Promise<[ApiErrorResponse, undefined] | [null, TagGroup[]]> => {
    const { contensis } = this;
    const groupId = isString(idOrOptions) ? idOrOptions : '';
    Logger.info(
      `Fetching ${
        isString(idOrOptions)
          ? `tag group ${Logger.standardText(groupId)}`
          : `tag groups${
              idOrOptions && 'q' in idOrOptions
                ? ` with query ${Logger.standardText(idOrOptions.q)}`
                : ''
            }`
      } in ${
        this.isSourceRepo ? '' : 'target environment '
      }${Logger.highlightText(contensis.alias)} ${Logger.standardText(
        contensis.projectId
      )}`
    );

    let groups;

    if (contensis.context.project)
      if (isString(idOrOptions)) {
        // Get the groups from the context if the repo is hydrated
        // e.g. in a migration or a simple get
        // Get the tag group from the context if the argument is a single id
        const group = contensis.context.tagGroups.find(
          (g) => g.id === idOrOptions
        );
        if (group) this.groups = replaceItemByKey(this.groups, group);
      } else if (!idOrOptions || !Object.keys(idOrOptions).length) {
        // Copy all tag groups from the context if the argument is empty
        this.groups = [...contensis.context.tagGroups];
      } else if ('ids' in idOrOptions && isArray(idOrOptions.ids)) {
        // Get the tag groups from the context if the argument is an array of ids
        this.groups = contensis.context.tagGroups.filter((g) =>
          idOrOptions.ids?.includes(g.id)
        );
      } else {
        // Get the groups from the api again if arguments are provided
        [this.error, groups] = await contensis.repo.GetTagGroups(idOrOptions);
        if (groups?.totalCount) {
          this.groups = replaceItemsById(this.groups, groups.items);
        }
      }
    else {
      // Get the groups from the api if the context is not populated
      if (isString(idOrOptions)) {
        [this.error, groups] =
          await this.contensis.repo.GetTagGroup(idOrOptions);
        if (groups) this.groups = replaceItemByKey(this.groups, groups);
      } else {
        [this.error, groups] =
          await this.contensis.repo.GetTagGroups(idOrOptions);
        if (groups?.totalCount) {
          const { ids = [] } = idOrOptions || {};
          if (ids.length)
            this.groups = replaceItemsById(
              this.groups,
              groups.items.filter((g) => ids.includes(g.id))
            );
          else this.groups = replaceItemsById(this.groups, groups.items);
        }
      }
    }
    return this.error ? [this.error, undefined] : [this.error, this.groups];
  };

  GetTags = async (
    getBy: GetTagsArgs = {}
  ): Promise<[ApiErrorResponse, undefined] | [null, Tag[]]> => {
    const { contensis } = this;
    const { groupId, id, ids, label, language } = getBy;

    // If the array of getBy.ids is supplied
    // it must be populated as an array of ids is a special search
    if (isArray(ids) && !ids.length) return [null, this.raw];

    const logMessage = `${
      isArray(ids)
        ? `Fetching ${pl('tag', ids.length, true)}`
        : id
          ? `Fetching tag with id ${Logger.standardText(id)}`
          : label
            ? `Fetching tags where label contains ${Logger.standardText(label)}`
            : `Fetching tags`
    }`;
    Logger.info(
      `${logMessage} ${
        id
          ? ''
          : groupId
            ? `in group ${Logger.standardText(groupId)} `
            : !ids?.length
              ? 'in all groups '
              : ''
      }from ${
        this.isSourceRepo ? '' : 'target environment '
      }${Logger.highlightText(contensis.alias)} ${Logger.standardText(
        contensis.projectId
      )}`,
      true
    );

    if (id) {
      const [err, tag] = await contensis.repo.GetTag(id);
      if (err) this.error = err;
      else this.raw = [tag];
    } else {
      const batchSize = 100;

      const allTags = await this.GetTagsAllPages(batchSize, {
        groupId,
        language,
        q: label,
      });

      if (allTags.length) {
        if (isArray(ids))
          this.raw = allTags.filter((tag) => ids.includes(tag.id));
        else this.raw = allTags;
      }
    }
    return this.error ? [this.error, undefined] : [this.error, this.raw];
  };

  GetTagsAllPages = async (
    pageSize = 200,
    getBy?: { groupId?: string; language?: string; q?: string }
  ) => {
    const { groupId, language, q } = getBy || {};
    let pageIndex = 0;
    let fetchMore = true;
    const items: Tag[] = [];

    // The while loop is supposed to fetch all tags until
    // we have retrieved the totalCount
    while (fetchMore) {
      const [err, { items: tags = [], pageCount = 0, totalCount = 0 } = {}] =
        await this.contensis.repo.GetTags({
          groupId,
          language,
          pageOptions: { pageIndex, pageSize },
          q,
        });
      if (err) this.error = err;
      else {
        if (this.outputProgress) {
          // Render progress if we're fetching 3 or more pages
          if (pageIndex === 0 && pageCount > 2 && !progress.current.total) {
            newProgressBar(
              ` Fetch [${this.contensis.project.id}] :bar :percent ${Logger.infoText(
                ':total :elapseds/:etas'
              )}`,
              {
                output: this.outputProgress,
                total: totalCount,
              }
            );
          }
          if (progress.current.total) progress.current.tick(pageSize);
        }

        items.push(...tags);
      }
      if (items.length >= totalCount) fetchMore = false;
      else pageIndex++;
    }
    completeProgressBar();
    return items;
  };

  CreateTag = async (
    groupId: string,
    label: string,
    language = 'en-GB',
    id?: string
  ): Promise<[ApiErrorResponse | null, Tag | undefined]> => {
    Logger.info(
      `Creating tag ${Logger.standardText(label)}${id ? ` ${Logger.infoText(id)}` : ''}`
    );
    const [error, tag] = await this.contensis.repo.CreateTag({
      id,
      groupId,
      label: {
        [language]: label,
      },
    });

    if (error) this.error = error;

    if (tag) this.raw.push(tag);
    return [error, tag];
  };

  CreateTagGroup = async (
    id: string,
    name: string,
    description?: string
  ): Promise<[ApiErrorResponse | null, TagGroup | undefined]> => {
    Logger.info(
      `Creating tag group ${Logger.standardText(name)}${id ? ` ${Logger.infoText(id)}` : ''}`
    );
    const [error, group] = await this.contensis.repo.CreateTagGroup({
      id,
      name,
      description,
    });

    if (error) this.error = error;

    if (group) this.groups.push(group);
    return [error, group];
  };

  UpdateTag = async (
    id: string,
    tag: IUpdateTag
  ): Promise<[ApiErrorResponse | null, Tag | undefined]> => {
    Logger.info(
      `Updating tag ${Logger.standardText(tag.value)} in group ${Logger.standardText(tag.groupId)}`
    );
    if (!this.error && this.raw.length > 0) {
      const existing = this.raw.find((tag) => tag.id === id) || {};

      const [error, updatedTag] = await this.contensis.repo.UpdateTag({
        ...existing,
        ...tag,
      });
      if (error) this.error = error;
      if (updatedTag) {
        this.raw = this.raw.filter((t) => t.id !== id);
        this.raw.push(updatedTag);
      }
      return [error, updatedTag];
    } else {
      return [this.error, undefined];
    }
  };

  UpdateTagGroup = async (
    id: string,
    tagGroup: IUpdateTagGroup
  ): Promise<[ApiErrorResponse | null, TagGroup | undefined]> => {
    Logger.info(
      `Updating tag group ${Logger.standardText(tagGroup.name)}${id ? ` ${Logger.infoText(id)}` : ''}`
    );

    if (!this.error && this.groups.length > 0) {
      const currentApiKey = this.groups.find((g) => g.id === id) || {};

      const [error, updatedGroup] = await this.contensis.repo.UpdateTagGroup({
        ...currentApiKey,
        ...tagGroup,
      });
      if (error) this.error = error;
      if (updatedGroup)
        this.groups = replaceItemByKey(this.groups, updatedGroup);

      return [error, updatedGroup];
    } else {
      return [this.error, undefined];
    }
  };

  RemoveTagGroup = async (
    id: string
  ): Promise<[ApiErrorResponse | null, undefined]> => {
    Logger.info(`Removing tag group ${Logger.standardText(id)}`);
    [this.error] = await this.contensis.repo.DeleteTagGroup(id);
    return [this.error, undefined];
  };

  RemoveTags = async (
    ids: string[]
  ): Promise<[ApiErrorResponse | null, undefined]> => {
    Logger.info(
      `Removing ${ids.length > 1 ? `${ids.length} tags` : `tag ${Logger.standardText(ids[0])}`}'`
    );
    [this.error] = await this.contensis.repo.DeleteTagsInGroup('', ids);
    return [this.error, undefined];
  };

  BuildTagGroups = async ({
    onError,
  }: {
    onError: (migrateTagGroup: TagGroupBuilder) => void;
  }) => {
    const { contensis, source } = this;
    for (const group of source.tags.groups) {
      // Build migrate tag group
      const tagGroupBuilder = new TagGroupBuilder(contensis, source, group);
      tagGroupBuilder.build();
      if (tagGroupBuilder.status === 'error') onError(tagGroupBuilder);
      this.migrateGroups.push(tagGroupBuilder);
    }
  };

  BuildTags = async ({
    onError,
  }: {
    onError: (migrateTag: TagBuilder) => void;
  }) => {
    const { contensis, source } = this;
    for (const tag of source.tags.raw) {
      // Build migrate tag
      const tagBuilder = new TagBuilder(contensis, source, tag);
      tagBuilder.build();
      if (tagBuilder.status === 'error') onError(tagBuilder);
      this.migrateTags.push(tagBuilder);
    }
  };
}

export default TagsService;
